import React, { useState } from 'react';
import {
  XMarkIcon,
  PauseCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

import GlassModal from './Common/GlassModal';

import { companyDateKey } from '../utils/dateFormatters';
/**
 * OnHoldModal
 *
 * Purpose: Capture details when job is put on hold
 * Industry Standard: ServiceTitan, Jobber, Housecall Pro all track hold reasons
 *
 * What it does:
 * 1. Asks WHY job is on hold (15 common reasons)
 * 2. Captures estimated resume date
 * 3. Optional notes
 * 4. Tracks who put it on hold
 * 5. Auto-frees technician calendar (via trigger)
 *
 * Part of: Phase 2 - Smart Status Progression
 * Competitive Advantage: Better tracking than competitors
 */

const OnHoldModal = ({
  isOpen,
  onClose,
  onConfirm,
  jobTitle
}) => {
  const [formData, setFormData] = useState({
    reason: '',
    estimatedResumeDate: '',
    notes: '',
    notifyCustomer: false
  });

  const [errors, setErrors] = useState({});

  if (!isOpen) return null;

  // Industry-standard on-hold reasons (researched from ServiceTitan, Jobber, Housecall Pro)
  const onHoldReasons = [
    // Customer-Related
    { value: 'customer_not_ready', label: '👤 Customer Not Ready', category: 'Customer' },
    { value: 'customer_requested_delay', label: '👤 Customer Requested Delay', category: 'Customer' },
    { value: 'customer_unavailable', label: '👤 Customer Unavailable', category: 'Customer' },
    { value: 'awaiting_customer_decision', label: '👤 Awaiting Customer Decision', category: 'Customer' },
    { value: 'awaiting_customer_payment', label: '💰 Awaiting Customer Payment', category: 'Customer' },

    // Parts/Materials
    { value: 'awaiting_parts', label: '📦 Awaiting Parts/Materials', category: 'Parts' },
    { value: 'parts_on_backorder', label: '📦 Parts on Backorder', category: 'Parts' },
    { value: 'wrong_parts_delivered', label: '📦 Wrong Parts Delivered', category: 'Parts' },

    // Permits/Approvals
    { value: 'awaiting_permit', label: '📋 Awaiting Permit/Approval', category: 'Permits' },
    { value: 'awaiting_inspection', label: '📋 Awaiting Inspection', category: 'Permits' },
    { value: 'awaiting_hoa_approval', label: '📋 Awaiting HOA Approval', category: 'Permits' },

    // Weather/Site Conditions
    { value: 'weather_delay', label: '🌧️ Weather Delay', category: 'Conditions' },
    { value: 'site_not_accessible', label: '🚧 Site Not Accessible', category: 'Conditions' },
    { value: 'unsafe_conditions', label: '⚠️ Unsafe Working Conditions', category: 'Conditions' },

    // Company/Resource Issues
    { value: 'technician_unavailable', label: '🔧 Technician Unavailable', category: 'Company' },
    { value: 'equipment_unavailable', label: '🛠️ Equipment Unavailable', category: 'Company' },
    { value: 'scheduling_conflict', label: '📅 Scheduling Conflict', category: 'Company' },

    // Other
    { value: 'other', label: '📝 Other (Specify in Notes)', category: 'Other' }
  ];

  // Group reasons by category
  const groupedReasons = onHoldReasons.reduce((acc, reason) => {
    if (!acc[reason.category]) {
      acc[reason.category] = [];
    }
    acc[reason.category].push(reason);
    return acc;
  }, {});

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.reason) {
      newErrors.reason = 'Please select a reason';
    }

    if (formData.reason === 'other' && !formData.notes.trim()) {
      newErrors.notes = 'Please provide details when selecting "Other"';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleConfirm = () => {
    if (!validate()) return;

    onConfirm({
      reason: formData.reason,
      estimatedResumeDate: formData.estimatedResumeDate || null,
      notes: formData.notes.trim(),
      notifyCustomer: formData.notifyCustomer
    });

    // Reset form
    setFormData({
      reason: '',
      estimatedResumeDate: '',
      notes: '',
      notifyCustomer: false
    });
    setErrors({});
  };

  const getReasonLabel = (value) => {
    const reason = onHoldReasons.find(r => r.value === value);
    return reason ? reason.label : value;
  };

  return (
    <GlassModal
      isOpen={isOpen}
      onClose={onClose}
      maxWidth="max-w-2xl"
      closeOnOverlay={false}
      header={(
        <>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-500/20 rounded-full flex items-center justify-center">
              <PauseCircleIcon className="w-6 h-6 text-orange-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white">Put Job On Hold</h2>
              <p className="text-sm text-gray-400 mt-0.5">{jobTitle}</p>
            </div>
          </div>
          <button type="button" aria-label="Close" title="Close" onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </>
      )}
      footer={(
        <>
          <button onClick={onClose} className="px-4 py-2 text-gray-300 hover:text-white font-medium transition-colors">
            Cancel
          </button>
          <button onClick={handleConfirm} className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 font-medium transition-colors">
            Put On Hold
          </button>
        </>
      )}
    >



          {/* Warning Message */}
          <div className="rounded-xl p-4 mb-6 bg-orange-500/10 border border-white/10 backdrop-blur-sm">
            <div className="flex items-start gap-3">
              <ExclamationTriangleIcon className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-orange-300 mb-1">
                  This will pause the job and free up the technician's calendar
                </p>
                <p className="text-xs text-orange-400">
                  The job will move to "On Hold" status and can be resumed later.
                </p>
              </div>
            </div>
          </div>

          {/* Reason Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-300 mb-3">
              Why is this job being put on hold? <span className="text-red-500">*</span>
            </label>

            <div className="space-y-4">
              {Object.entries(groupedReasons).map(([category, reasons]) => (
                <div key={category}>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                    {category}
                  </p>
                  <div className="space-y-2">
                    {reasons.map((reason) => (
                      <label
                        key={reason.value}
                        className={`flex items-center p-3 border-2 rounded-lg cursor-pointer transition-all ${
                          formData.reason === reason.value
                            ? 'border-orange-500 bg-orange-500/10'
                            : 'border-white/20 hover:border-white/40'
                        }`}
                      >
                        <input
                          type="radio"
                          name="on-hold-reason"
                          value={reason.value}
                          checked={formData.reason === reason.value}
                          onChange={(e) => handleChange('reason', e.target.value)}
                          className="mr-3"
                        />
                        <span className="text-sm text-white">{reason.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {errors.reason && (
              <p className="mt-2 text-sm text-red-600">{errors.reason}</p>
            )}
          </div>

          {/* Estimated Resume Date */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Estimated Resume Date (Optional)
            </label>
            <input
              type="date"
              value={formData.estimatedResumeDate}
              onChange={(e) => handleChange('estimatedResumeDate', e.target.value)}
              min={companyDateKey()}
              className="w-full px-4 py-2 border border-white/20 rounded-lg bg-white/10 text-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500 [color-scheme:dark]"
            />
            <p className="mt-1 text-xs text-gray-400">
              When do you expect to resume this job?
            </p>
          </div>

          {/* Additional Notes */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Additional Notes {formData.reason === 'other' && <span className="text-red-500">*</span>}
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Provide any additional details about why this job is on hold..."
              rows={4}
              className={`w-full px-4 py-2 border rounded-lg bg-white/10 text-white placeholder-gray-500 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none ${
                errors.notes ? 'border-red-400' : 'border-white/20'
              }`}
            />
            {errors.notes && (
              <p className="mt-2 text-sm text-red-600">{errors.notes}</p>
            )}
          </div>

          {/* Notify Customer Checkbox */}
          <div className="mb-6">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.notifyCustomer}
                onChange={(e) => handleChange('notifyCustomer', e.target.checked)}
                className="mt-1"
              />
              <div>
                <span className="text-sm font-medium text-white">
                  Notify customer about this delay
                </span>
                <p className="text-xs text-gray-400 mt-0.5">
                  Send an automatic notification to the customer explaining the delay
                </p>
              </div>
            </label>
          </div>



    </GlassModal>
  );
};

export default OnHoldModal;
